import React, { useEffect } from 'react';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { PFE_ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import { httpClient } from 'helpers/http';
import { reportError } from 'helpers/error-logging';

import URLS from 'constants/urls';

import { openDecagonChat } from 'components/Decagon';
import Button from 'components/Button';
import Message from 'components/Message';
import { Form, Input } from 'components/HookForm';

import { mfaFormMessages as messages } from './Auth.messages';
import { mfaVerificationSuccess } from './Auth.saga';
import './Auth.scss';

const { element } = bem('auth');

const formatRecoveryCode = value => {
  const cleanValue = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  const limitedValue = cleanValue.slice(0, 10);

  if (limitedValue.length > 5) {
    return `${limitedValue.slice(0, 5)}-${limitedValue.slice(5)}`;
  }

  return limitedValue;
};

function MFARecoveryCode() {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const continuationToken = location.state?.mfaContinuationToken;

  useEffect(() => {
    if (!continuationToken) {
      navigate(makePath.login(), { replace: true, state: { error: 'mfa_token_missing' } });
    }
  }, [continuationToken, navigate]);

  const handleSubmit = async (values, { rejectSubmit }) => {
    try {
      const { data } = await httpClient.post(URLS.user.mfa.recoverCode, {
        mfa_continuation_token: continuationToken,
        private_recovery_code: values.recoveryCode,
      });
      dispatch(mfaVerificationSuccess(data));
    } catch (error) {
      rejectSubmit(null, {
        recoveryCode: intl.formatMessage(messages.recoveryCodeNotValid),
      });
      reportError('Recovery code validation failed', error);
    }
  };

  return (
    <div className={element('wrapper')}>
      <Isvg className={element('logo')} src={PFE_ICONS.logotype} />

      <Message message={messages.accountRecovery} as="h1" className={element('header')} />
      <Message message={messages.accountRecoverySubtitle} as="p" className={element('hint')} />

      <Form
        className={element('recovery-form')}
        defaultValues={{ recoveryCode: '' }}
        useFormOverrides={{ mode: 'onChange' }}
        onSubmit={handleSubmit}>
        {({ formState: { errors, isSubmitting }, clearErrors, resetField }) => {
          const hasError = !!errors.recoveryCode;
          return (
            <>
              <Input
                name="recoveryCode"
                type="text"
                className={element('code-input', { loading: isSubmitting })}
                maxLength={11}
                disabled={isSubmitting}
                placeholder={messages.recoveryTextPlaceholder}
                format={formatRecoveryCode}
                onFocus={() => {
                  if (hasError) {
                    clearErrors('recoveryCode');
                    resetField('recoveryCode');
                  }
                }}
              />
              <Button type="submit" waiting={isSubmitting}>
                <Message message={messages.verify} />
              </Button>
            </>
          );
        }}
      </Form>

      <div className={element('mfa-quick-link-container')}>
        <Message message={messages.backToLogin} as={Link} to={makePath.login()} className={element('quick-link')} />
        <Message
          as={Button}
          className={element('quick-link')}
          message={messages.support}
          onClick={openDecagonChat}
          styled="inline-link"
          type={Button.TYPES.BUTTON}
        />
      </div>
    </div>
  );
}

export default MFARecoveryCode;
